import isToday from 'date-fns/isToday';
import type React from 'react';
import { useTranslations } from '@vocab/react';
import type { IJob } from '../../../../stores/JobStore/Job';
import type { IUser } from '../../../../stores/UserStore';
import translations from '../.vocab';
import {
  JobAdType,
  JobStatus,
} from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import { JobPostChannel } from 'src/stores/JobStore/types';
import { isANZSite } from 'src/utils/site';
import type { EmployerSiteNames } from 'src/siteConfig';
import { getAnzCsPhoneNumberFromSite } from '../../../../utils/getCsContactMethodMessageFromSite.ts';

export interface ActionStatus {
  disabled: boolean;
  disableDescription?: React.ReactNode;
}

export interface JobActionStatus {
  viewActionStatus: ActionStatus;
  editActionStatus: ActionStatus;
  notificationActionStatus: ActionStatus;
  teamAccessActionStatus: ActionStatus;
  shareActionStatus: ActionStatus;
  refreshActionStatus: ActionStatus;
  copyActionStatus: ActionStatus;
  expireActionStatus: ActionStatus;
}

export const ENABLED = { disabled: false };
export const DISABLED = { disabled: true };

export const getDisableStatus = (
  disableDescription: React.ReactNode,
): ActionStatus => ({
  disabled: true,
  disableDescription,
});
const isListingStartDateToday = (job: IJob) => {
  const { listingStartedDate } = job.jobInformation;

  if (listingStartedDate === '') {
    return false;
  }

  return isToday(new Date(listingStartedDate));
};

const isIndirectJob = (job: IJob) =>
  job.jobInformation.postChannel === JobPostChannel.INDIRECT;

const isDisabledWithMessageCallContactSupport = (user: IUser): boolean =>
  user.showOnCreditHoldMessage;

const isDisabledWithMessageContactAdmin = (user: IUser): boolean =>
  !user.canCreateJobs;

const isDisabledDueToPremiumJobAd = (job: IJob): boolean =>
  job.jobInformation?.adType === JobAdType.PREMIUM;

const viewActionStatus = (job: IJob): ActionStatus => {
  if (
    ![JobStatus.ACTIVE, JobStatus.EXPIRED].includes(job.jobInformation.status)
  ) {
    return DISABLED;
  }

  return ENABLED;
};

interface EditActionStatusOptions {
  allowForIndirectJob: boolean;
  editWhileExpired: boolean;
}

const useEditActionStatus = (
  job: IJob,
  user: IUser,
  isAnz: boolean,
  csPhoneNumber: string,
  options: EditActionStatusOptions = {
    allowForIndirectJob: false,
    editWhileExpired: false,
  },
): ActionStatus => {
  const { t } = useTranslations(translations);

  if (!options.allowForIndirectJob && isIndirectJob(job)) {
    return DISABLED;
  }

  if (
    isDisabledWithMessageCallContactSupport(user) &&
    !job.userStore.user.isCsView
  ) {
    const errorMessage = isAnz
      ? t(
          'To edit this ad reach out to our Customer Service - with phone number',
          {
            phoneNumber: csPhoneNumber,
          },
        )
      : t('To edit this ad reach out to our Customer Service team');
    return getDisableStatus(errorMessage);
  }

  if (
    [
      JobStatus.PENDING,
      JobStatus.PENDING_ACCOUNT_APPROVAL,
      JobStatus.SUSPENDED,
    ].includes(job.jobInformation.status) &&
    !job.userStore.user.isCsView
  ) {
    const errorMessage = isAnz
      ? t(
          'To edit this ad reach out to our Customer Service - with phone number',
          {
            phoneNumber: csPhoneNumber,
          },
        )
      : t('To edit this ad reach out to our Customer Service team');
    return getDisableStatus(errorMessage);
  }

  if (
    !options.editWhileExpired &&
    job.jobInformation.status === JobStatus.EXPIRED
  ) {
    return getDisableStatus(t('Expired ads cannot be edited'));
  }

  return ENABLED;
};

const useNotificationActionStatus = (
  job: IJob,
  user: IUser,
  isAnz: boolean,
  csPhoneNumber: string,
): ActionStatus => useEditActionStatus(job, user, isAnz, csPhoneNumber);

const useTeamAccessActionStatus = (
  job: IJob,
  user: IUser,
  isAnz: boolean,
  csPhoneNumber: string,
): ActionStatus =>
  useEditActionStatus(job, user, isAnz, csPhoneNumber, {
    allowForIndirectJob: true,
    editWhileExpired: true,
  });

const useRefreshActionStatus = (
  job: IJob,
  user: IUser,
  isAnz: boolean,
  csPhoneNumber: string,
): ActionStatus => {
  const { t } = useTranslations(translations);
  const errorMessage = isAnz
    ? t(
        'To refresh this ad reach out to our Customer Service - with Phone Number',
        {
          phoneNumber: csPhoneNumber,
        },
      )
    : t('To refresh this ad reach out to our Customer Service team');
  if (isIndirectJob(job)) {
    return DISABLED;
  }

  if (isDisabledDueToPremiumJobAd(job)) {
    return getDisableStatus(t('Refresh is currently unavailable'));
  }

  if (isDisabledWithMessageCallContactSupport(user)) {
    return getDisableStatus(errorMessage);
  }

  if (isDisabledWithMessageContactAdmin(user)) {
    return getDisableStatus(
      t('For access to refresh ads contact your account Administrator'),
    );
  }

  if (
    [
      JobStatus.PENDING,
      JobStatus.PENDING_ACCOUNT_APPROVAL,
      JobStatus.SUSPENDED,
    ].includes(job.jobInformation.status)
  ) {
    return getDisableStatus(errorMessage);
  }

  if (job.jobInformation.billableApplicants) {
    return getDisableStatus(t("Refresh isn't available for this ad product"));
  }

  if (job.jobInformation.status === JobStatus.EXPIRED) {
    return getDisableStatus(t('Expired ads cannot be refreshed'));
  }

  if (isListingStartDateToday(job)) {
    return getDisableStatus(t('Posted today'));
  }

  return ENABLED;
};

const useShareActionStatus = (
  job: IJob,
  user: IUser,
  isAnz: boolean,
  csPhoneNumber: string,
): ActionStatus => {
  const { t } = useTranslations(translations);
  const errorMessage = isAnz
    ? t(
        'To share this ad reach out to our Customer Service - with Phone Number',
        {
          phoneNumber: csPhoneNumber,
        },
      )
    : t('To share this ad reach out to our Customer Service team');
  if (isDisabledWithMessageCallContactSupport(user)) {
    return getDisableStatus(errorMessage);
  }

  if (isDisabledWithMessageContactAdmin(user)) {
    return getDisableStatus(
      t('For access to share ads contact your account Administrator'),
    );
  }

  if (
    [
      JobStatus.PENDING,
      JobStatus.PENDING_ACCOUNT_APPROVAL,
      JobStatus.SUSPENDED,
    ].includes(job.jobInformation.status)
  ) {
    return getDisableStatus(errorMessage);
  }

  if (job.jobInformation.status === JobStatus.EXPIRED) {
    return getDisableStatus(t('Expired ads cannot be shared'));
  }

  return ENABLED;
};

const useCopyActionStatus = (
  job: IJob,
  user: IUser,
  isAnz: boolean,
  csPhoneNumber: string,
): ActionStatus => {
  const { t } = useTranslations(translations);
  const errorMessage = isAnz
    ? t(
        'To copy this ad reach out to our Customer Service - with Phone Number',
        {
          phoneNumber: csPhoneNumber,
        },
      )
    : t('To copy this ad reach out to our Customer Service team');
  if (isDisabledWithMessageCallContactSupport(user)) {
    return getDisableStatus(errorMessage);
  }

  if (isDisabledWithMessageContactAdmin(user)) {
    return getDisableStatus(
      t('For access to copy ads contact your account Administrator'),
    );
  }

  return ENABLED;
};

const useExpireActionStatus = (
  job: IJob,
  isAnz: boolean,
  csPhoneNumber: string,
): ActionStatus => {
  const { t } = useTranslations(translations);
  const errorMessage = isAnz
    ? t(
        'To expire this ad reach out to our Customer Service - with Phone Number',
        {
          phoneNumber: csPhoneNumber,
        },
      )
    : t('To expire this ad reach out to our Customer Service team');
  if (isIndirectJob(job)) {
    return DISABLED;
  }

  if (
    [
      JobStatus.PENDING,
      JobStatus.PENDING_ACCOUNT_APPROVAL,
      JobStatus.SUSPENDED,
    ].includes(job.jobInformation.status) &&
    !job.userStore.user.isCsView
  ) {
    return getDisableStatus(errorMessage);
  }

  if (job.jobInformation.status === JobStatus.EXPIRED) {
    return getDisableStatus(t('This ad is already expired'));
  }

  return ENABLED;
};

const useJobActionStatus = (
  job: IJob,
  site: EmployerSiteNames,
): JobActionStatus => {
  const isAnz = isANZSite(site);
  const { userStore } = job;
  const user = userStore.user;

  const csPhoneNumber = getAnzCsPhoneNumberFromSite(site);

  return {
    viewActionStatus: viewActionStatus(job),
    editActionStatus: useEditActionStatus(job, user, isAnz, csPhoneNumber),
    notificationActionStatus: useNotificationActionStatus(
      job,
      user,
      isAnz,
      csPhoneNumber,
    ),
    teamAccessActionStatus: useTeamAccessActionStatus(
      job,
      user,
      isAnz,
      csPhoneNumber,
    ),
    shareActionStatus: useShareActionStatus(job, user, isAnz, csPhoneNumber),
    refreshActionStatus: useRefreshActionStatus(
      job,
      user,
      isAnz,
      csPhoneNumber,
    ),
    copyActionStatus: useCopyActionStatus(job, user, isAnz, csPhoneNumber),
    expireActionStatus: useExpireActionStatus(job, isAnz, csPhoneNumber),
  };
};

export default useJobActionStatus;
